'use client';

import { useCallback, useEffect, useRef } from 'react';

type Callback = () => void;

/**
 * A React hook for managing timeouts with enhanced control and safety features.
 *
 * @param {Callback} callback - The function to be executed after the timeout period
 * @param {number | null} delay - Time in milliseconds to wait before executing the callback. Use `null` to disable the timeout
 *
 * @returns {readonly [(newDelay?: number) => void, () => void]} - A resulting tuple with two functions:
 * - `start`: Starts or reset the timeout. Optionally accepts a new delay duration
 * - `stop`: Clears the current timeout
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [start, stop] = useTimeout(() => {
 *     console.log('Timeout executed!');
 *   }, 1000);
 *
 *   return (
 *     <div>
 *       <button onClick={() => start(2000)}>Start 2s timer</button>
 *       <button onClick={stop}>Stop timer</button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @remarks
 * - The timeout is automatically cleared when the component unmounts
 * - The callback reference is always up-to-date, even if it changes after the timeout starts
 * - Starting a new timeout automatically clears any existing timeout
 * - Use `null` delay to initialize a disabled timeout
 */

export function useTimeout(
  callback: Callback,
  delay: number | null
): readonly [(newDelay?: number) => void, () => void] {
  const savedCallback = useRef<Callback | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Clear the timeout
  const stop = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  // Start/Reset the timeout with optional new duration
  const start = useCallback(
    (newDelay?: number) => {
      stop();
      if ((newDelay ?? delay) !== null && savedCallback.current) {
        timeoutRef.current = setTimeout(
          savedCallback.current,
          newDelay ?? delay ?? 0
        );
      }
    },
    [delay, stop]
  );

  // Set up the initial timeout
  useEffect(() => {
    if (delay !== null) {
      timeoutRef.current = setTimeout(() => {
        if (savedCallback.current) {
          savedCallback.current();
        }
      }, delay);
      return stop;
    }
    return undefined;
  }, [delay, stop]);

  return [start, stop] as const;
}
